import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { connectWallet, getCurrentAccount, hasMetamask, ensurePolygon } from "./web3";

const Ctx = createContext(null);

export function WalletProvider({ children }) {
    const [account, setAccount] = useState(null);
    const [chainId, setChainId] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // Intentional one-shot setup: read current wallet state and subscribe
        // to MetaMask events. window.ethereum and the setters from useState
        // are stable references, no need to retrigger.
        let cleanup = () => {};
        (async () => {
            if (!hasMetamask()) return;
            try {
                const a = await getCurrentAccount();
                setAccount(a);
                const cid = await window.ethereum.request({ method: "eth_chainId" });
                setChainId(cid);
                const onAccounts = (accs) => setAccount(accs[0] || null);
                const onChain = (cid2) => setChainId(cid2);
                window.ethereum.on("accountsChanged", onAccounts);
                window.ethereum.on("chainChanged", onChain);
                cleanup = () => {
                    if (window.ethereum?.removeListener) {
                        window.ethereum.removeListener("accountsChanged", onAccounts);
                        window.ethereum.removeListener("chainChanged", onChain);
                    }
                };
            } catch (err) {
                console.warn("Wallet init failed:", err);
            }
        })();
        return () => cleanup();
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const connect = async () => {
        setBusy(true);
        try {
            const a = await connectWallet();
            await ensurePolygon();
            setAccount(a);
        } finally {
            setBusy(false);
        }
    };

    const disconnect = () => setAccount(null);

    // Memoize context value so child consumers don't re-render on parent updates.
    const value = useMemo(
        () => ({ account, chainId, busy, connect, disconnect }),
        [account, chainId, busy]
    );

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useWallet = () => useContext(Ctx);
